import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Ban,
  KeyRound,
  MailPlus,
  RefreshCw,
  RotateCcw,
  Trash2,
  UserPlus,
} from 'lucide-react';

import { ovhApi } from '../lib/ovhApi';

const ROLE_LABELS = {
  commercial: 'Commercial',
  logistics: 'Logistique',
  admin: 'Administrateur',
};

const formatDateTime = (value) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

async function invokeUserAction(action, payload = {}) {
  if (action === 'list') {
    return { users: await ovhApi.listUsers() };
  }

  if (action === 'invite') {
    return ovhApi.inviteUser(payload);
  }

  return ovhApi.userAction(action, payload);
}

export default function UserManagement({
  currentUserId,
  currentRole,
  onBack,
  onNotice,
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    full_name: '',
    role: 'commercial',
  });

  const isAdmin = currentRole === 'admin';

  async function loadUsers() {
    setLoading(true);

    try {
      const data = await invokeUserAction('list');
      setUsers(data.users || []);
    } catch (error) {
      onNotice(`Chargement des utilisateurs impossible : ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function submitInvitation(event) {
    event.preventDefault();
    setInviteSubmitting(true);

    try {
      const data = await invokeUserAction('invite', {
        ...inviteForm,
      });

      onNotice(data.message || 'Invitation envoyée.');
      setInviteForm({
        email: '',
        full_name: '',
        role: 'commercial',
      });
      setInviteOpen(false);
      await loadUsers();
    } catch (error) {
      onNotice(`Invitation impossible : ${error.message}`);
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function runAction(action, user, options = {}) {
    const confirmations = {
      disable: `Désactiver l’accès de ${user.email} ?`,
      reactivate: `Réactiver l’accès de ${user.email} ?`,
      delete: `Supprimer définitivement le compte ${user.email} ? Cette action est irréversible.`,
      'resend-invitation': `Renvoyer une invitation à ${user.email} ?`,
      'send-password-reset': `Envoyer un lien de réinitialisation à ${user.email} ?`,
      'update-role': `Attribuer le rôle « ${ROLE_LABELS[options.role]} » à ${user.email} ?`,
    };

    if (!window.confirm(confirmations[action])) return;

    setBusyUserId(user.id);

    try {
      const data = await invokeUserAction(action, {
        user_id: user.id,
        ...options,
      });

      onNotice(data.message || 'Action réalisée.');
      await loadUsers();
    } catch (error) {
      onNotice(`Action impossible : ${error.message}`);
    } finally {
      setBusyUserId('');
    }
  }

  return (
    <section className="users-page">
      <div className="users-page-header">
        <div>
          <button type="button" className="secondary" onClick={onBack}>
            <ArrowLeft size={17} />
            Retour aux expéditions
          </button>

          <h2>Utilisateurs</h2>
          <p>
            Gérez les invitations, les rôles et les accès à l’application.
          </p>
        </div>

        <div className="users-page-actions">
          <button
            type="button"
            className="secondary"
            onClick={loadUsers}
            disabled={loading}
          >
            <RefreshCw size={17} className={loading ? 'spinning' : ''} />
            Actualiser
          </button>

          <button
            type="button"
            className="primary"
            onClick={() => setInviteOpen((value) => !value)}
          >
            <UserPlus size={17} />
            Inviter un utilisateur
          </button>
        </div>
      </div>

      {inviteOpen && (
        <form className="panel invite-form" onSubmit={submitInvitation}>
          <label>
            Adresse e-mail
            <input
              type="email"
              value={inviteForm.email}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              required
            />
          </label>

          <label>
            Nom complet
            <input
              type="text"
              value={inviteForm.full_name}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  full_name: event.target.value,
                }))
              }
            />
          </label>

          <label>
            Rôle
            <select
              value={inviteForm.role}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  role: event.target.value,
                }))
              }
            >
              <option value="commercial">Commercial</option>
              <option value="logistics">Logistique</option>
              {isAdmin && <option value="admin">Administrateur</option>}
            </select>
          </label>

          <button
            type="submit"
            className="primary"
            disabled={inviteSubmitting}
          >
            <MailPlus size={17} />
            {inviteSubmitting ? 'Envoi…' : 'Envoyer l’invitation'}
          </button>
        </form>
      )}

      <div className="panel table-wrap users-table">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Adresse e-mail</th>
                <th>Rôle</th>
                <th>Date de création</th>
                <th>État</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {users.map((user) => {
                const isCurrentUser =
                  user.is_current_user || user.id === currentUserId;
                const isBusy = busyUserId === user.id;

                return (
                  <tr key={user.id}>
                    <td data-label="Utilisateur">
                      <strong>{user.email}</strong>
                      {user.full_name && <small>{user.full_name}</small>}
                      {isCurrentUser && <small>Votre compte</small>}
                    </td>

                    <td data-label="Rôle">
                      <select
                        value={user.role}
                        disabled={!isAdmin || isCurrentUser || isBusy}
                        onChange={(event) =>
                          runAction('update-role', user, {
                            role: event.target.value,
                          })
                        }
                      >
                        <option value="commercial">Commercial</option>
                        <option value="logistics">Logistique</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </td>

                    <td data-label="Date de création">
                      {formatDateTime(user.created_at)}
                    </td>

                    <td data-label="État">
                      <span
                        className={`badge ${
                          user.disabled_at
                            ? 'badge-non_compliant'
                            : user.password_setup_required
                              ? 'badge-pending_preparation'
                            : user.confirmed_at
                              ? 'badge-compliant'
                              : 'badge-pending_preparation'
                        }`}
                      >
                        <span className="badge-dot" />
                        {user.disabled_at
                          ? 'Désactivé'
                          : user.password_setup_required
                            ? 'Mot de passe à créer'
                          : user.confirmed_at
                            ? 'Actif'
                            : 'Invitation en attente'}
                      </span>
                    </td>

                    <td data-label="Actions">
                      <div className="user-actions">
                        {!user.confirmed_at && (
                          <button
                            type="button"
                            className="secondary"
                            title="Renvoyer l’invitation"
                            disabled={isBusy}
                            onClick={() =>
                              runAction('resend-invitation', user)
                            }
                          >
                            <MailPlus size={16} />
                          </button>
                        )}

                        <button
                          type="button"
                          className="secondary"
                          title="Envoyer une réinitialisation du mot de passe"
                          disabled={isBusy}
                          onClick={() =>
                            runAction('send-password-reset', user)
                          }
                        >
                          <KeyRound size={16} />
                        </button>

                        {isAdmin && !isCurrentUser && (
                          <>
                            <button
                              type="button"
                              className="secondary"
                              title={
                                user.disabled_at
                                  ? 'Réactiver le compte'
                                  : 'Désactiver le compte'
                              }
                              disabled={isBusy}
                              onClick={() =>
                                runAction(
                                  user.disabled_at
                                    ? 'reactivate'
                                    : 'disable',
                                  user
                                )
                              }
                            >
                              {user.disabled_at ? (
                                <RotateCcw size={16} />
                              ) : (
                                <Ban size={16} />
                              )}
                            </button>

                            <button
                              type="button"
                              className="danger"
                              title="Supprimer définitivement"
                              disabled={isBusy}
                              onClick={() => runAction('delete', user)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!loading && !users.length && (
          <div className="empty">Aucun utilisateur à afficher.</div>
        )}

        {loading && (
          <div className="empty">Chargement des utilisateurs…</div>
        )}
      </div>
    </section>
  );
}
